import React, { useState } from 'react';
import Card from '../ui/Card';
import Badge from '../ui/Badge';
import Button from '../ui/Button';
import { formatDate, formatDateTime } from '../../utils/dateFormat';

export default function SafeguardingContextPanel({
  flags = [],
  incidentsCount = 0,
  onViewIncidents,
  notes = [],
  auditHistory = [],
  isLoading = false,
  error = ''
}) {
  const [open, setOpen] = useState(false);
  const viewIncidents = onViewIncidents || (() => {});

  const content = (
    <div className="space-y-4">
      <Card className="p-4">
        <div className="flex items-center justify-between">
          <h4 className="sp-value" style={{ fontWeight: 600 }}>Safeguarding History</h4>
          <Badge variant={flags.length ? 'review' : 'inactive'}>{flags.length}</Badge>
        </div>
        <div className="mt-3 space-y-2">
          {flags.length === 0 && <p className="text-sm sp-muted">No safeguarding flags recorded.</p>}
          {flags.slice(0, 5).map((flag) => (
            <div key={flag.id} className="sp-panel sp-panel--muted p-3">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm sp-value" style={{ fontWeight: 600 }}>{flag.type}</p>
                  <p className="text-xs sp-muted">{formatDate(flag.date)} · {flag.category}</p>
                </div>
                <Badge variant={flag.status === 'open' ? 'review' : 'inactive'}>{flag.status}</Badge>
              </div>
              {flag.description && (
                <p className="text-xs sp-muted mt-2">{flag.description}</p>
              )}
            </div>
          ))}
        </div>
      </Card>

      <Card className="p-4">
        <div className="flex items-center justify-between">
          <div>
            <h4 className="sp-value" style={{ fontWeight: 600 }}>Open Incidents</h4>
            <p className="text-xs sp-muted">Linked incidents requiring review</p>
          </div>
          <Badge variant={incidentsCount > 0 ? 'review' : 'inactive'}>{incidentsCount}</Badge>
        </div>
        <Button
          size="sm"
          variant="secondary"
          className="mt-3"
          onClick={viewIncidents}
          type="button"
          disabled={!onViewIncidents}
        >
          View incidents
        </Button>
      </Card>

      <Card className="p-4">
        <h4 className="sp-value" style={{ fontWeight: 600 }}>Latest Notes</h4>
        <div className="mt-3 space-y-2">
          {notes.length === 0 && <p className="text-sm sp-muted">No notes recorded.</p>}
          {notes.slice(0, 3).map((note) => (
              <div key={note.id} className="sp-panel sp-panel--muted p-3">
              <p className="text-sm sp-value">{note.content}</p>
              <p className="text-xs sp-muted">{formatDateTime(note.createdAt)} · {note.createdBy}</p>
            </div>
          ))}
        </div>
      </Card>

      <Card className="p-4">
        <h4 className="sp-value" style={{ fontWeight: 600 }}>Audit Highlights</h4>
        <div className="mt-3 space-y-2">
          {auditHistory.length === 0 && <p className="text-sm sp-muted">No audit changes recorded.</p>}
          {auditHistory.slice(0, 4).map((item, index) => (
            <div key={`${item.date}-${index}`} className="sp-panel sp-panel--muted p-3">
              <p className="text-sm sp-value">{item.status}</p>
              <p className="text-xs sp-muted">{formatDate(item.date)} · {item.by || 'System'}</p>
            </div>
          ))}
        </div>
      </Card>
    </div>
  );

  if (isLoading) {
    return <div className="text-sm sp-muted">Loading safeguarding context…</div>;
  }
  if (error) {
    return <div className="sp-alert sp-alert--critical text-sm">{error}</div>;
  }

  return (
    <aside className="lg:sticky lg:top-24 space-y-3" aria-label="Safeguarding context panel">
      <div className="lg:hidden">
        <Button
          variant="secondary"
          type="button"
          onClick={() => setOpen((prev) => !prev)}
          aria-expanded={open}
        >
          {open ? 'Hide safeguarding context' : 'Show safeguarding context'}
        </Button>
      </div>
      <div className={`${open ? 'block' : 'hidden'} lg:block`}>{content}</div>
    </aside>
  );
}
